<template>
  <!-- Sign-up, log-in and the contact popup are triggered by data-auth and
       data-demo attributes, as in the published page, and handled here for the
       whole page. That also covers the map's popups, which Leaflet renders as
       plain HTML. -->
  <div class="landing-page" @click="onAction">
    <landing-nav />

    <main>
      <hero-section @go="scrollTo" />
      <trust-marquee />
      <how-it-works />
      <platform-section @go="scrollTo" />
      <inside-section />
      <e2v-section />
      <marketplace-section @route="goRoute" />
      <adverpeople-section />
      <stats-band />
      <hardware-section />
      <pricing-section @go="scrollTo" />
      <faq-section />
      <closing-section />
    </main>

    <landing-footer @go="scrollTo" @route="goRoute" />

    <demo-modal ref="demo" />
  </div>
</template>

<script>
import LandingNav from "./LandingNav.vue";
import HeroSection from "./HeroSection.vue";
import TrustMarquee from "./TrustMarquee.vue";
import HowItWorks from "./HowItWorks.vue";
import PlatformSection from "./PlatformSection.vue";
import InsideSection from "./InsideSection.vue";
import E2vSection from "./E2vSection.vue";
import MarketplaceSection from "./MarketplaceSection.vue";
import AdverpeopleSection from "./AdverpeopleSection.vue";
import StatsBand from "./StatsBand.vue";
import HardwareSection from "./HardwareSection.vue";
import PricingSection from "./PricingSection.vue";
import FaqSection from "./FaqSection.vue";
import ClosingSection from "./ClosingSection.vue";
import LandingFooter from "./LandingFooter.vue";
import DemoModal from "./DemoModal.vue";
import { initLandingDemos } from "./demos";
import { initLandingI18n } from "./landingI18n";

export default {
  name: "LandingPage",
  components: {
    LandingNav,
    HeroSection,
    TrustMarquee,
    HowItWorks,
    PlatformSection,
    InsideSection,
    E2vSection,
    MarketplaceSection,
    AdverpeopleSection,
    StatsBand,
    HardwareSection,
    PricingSection,
    FaqSection,
    ClosingSection,
    LandingFooter,
    DemoModal,
  },
  mounted() {
    this.loadFonts();
    // Marks the app shell while this page is on screen. It lets the page scroll
    // (the shell sets body{overflow:hidden} for its own inner-scroll layout) and
    // restyles the shared login and register modals to match this page. Both are
    // reverted on destroy so the rest of the app is unaffected.
    document.body.classList.add("landing-page-active");
    document.documentElement.classList.add("landing-page-active");

    // The demos read rendered markup (the product mockups clone the header
    // logo), so they run once the whole page is in the DOM.
    this.$nextTick(() => {
      this.teardownDemos = initLandingDemos();
      // After the demos, so the generated mockups are translated too (later
      // changes are picked up by the module's own observer).
      this.teardownI18n = initLandingI18n(this.$el, {
        // The footer switch also switches the app's own text, such as the
        // login and register popups, without the reload App.vue's switch does.
        onSwitch: (locale) => {
          this.$i18n.locale = locale;
        },
      });
    });
  },
  beforeDestroy() {
    document.body.classList.remove("landing-page-active");
    document.documentElement.classList.remove("landing-page-active");
    if (this.teardownDemos) {
      this.teardownDemos();
      this.teardownDemos = null;
    }
    if (this.teardownI18n) {
      this.teardownI18n();
      this.teardownI18n = null;
    }
  },
  data() {
    return { teardownDemos: null, teardownI18n: null };
  },
  methods: {
    // Poppins and Inter came from a <link> in the static page's <head>. The
    // component injects them once so the page keeps its typography.
    loadFonts() {
      const href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Inter:wght@400;500;600&display=swap";
      if (document.querySelector(`link[href="${href}"]`)) return;
      [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
        { rel: "stylesheet", href },
      ].forEach((attrs) => {
        const l = document.createElement("link");
        Object.assign(l, attrs);
        document.head.appendChild(l);
      });
    },
    // The app uses a hash router, so in-page anchors are handled as scrolls
    // rather than hrefs, which the router would otherwise treat as routes.
    scrollTo(href) {
      // getElementById takes the id as a value rather than parsing a selector,
      // so an id containing quotes or brackets cannot change what is matched.
      const target = document.getElementById(href.slice(1));
      if (target && this.$el.contains(target)) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    // Footer legal links and "Find screens to book": stay inside the SPA
    // rather than reloading it, and on this host rather than adverpost.com.
    goRoute(path) {
      this.$router.push(path).catch(() => {});
    },
    // data-auth="register|login" asks Landing.vue to open the matching modal;
    // data-demo="<topic>" opens the contact popup with that topic's label.
    onAction(e) {
      const auth = e.target.closest("[data-auth]");
      if (auth) {
        e.preventDefault();
        this.$refs.demo.close(false);
        this.$emit("auth", auth.dataset.auth);
        return;
      }
      const demo = e.target.closest("[data-demo]");
      if (demo) {
        e.preventDefault();
        this.$refs.demo.open(demo.dataset.demo, demo);
      }
    },
  },
};
</script>

<style lang="scss">
// Generated from the published page; regenerate rather than edit.
@import "~@/assets/landing/artifact";
@import "~@/assets/landing/photos";
// This app's own layers, applied after the published styles.
@import "~@/assets/landing/shell";
@import "~@/assets/landing/theme-switch";
@import "~@/assets/landing/phone";
@import "~@/assets/landing/auth-modal";
// Dark mode, which the handoff does not have: the published page's earlier dark
// theme, then this app's fixes on top of it.
@import "~@/assets/landing/dark-theme";
@import "~@/assets/landing/dark";
</style>
